/**
 * Central filter state hook.
 *
 * Uses useSearchParams() as single source of truth (DD-1).
 * Replaces 15+ individual handleXxxChange callbacks in GrantsCalls.tsx.
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 3.3)
 */

import { useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { FilterPlugin } from "./types";
import { getAdvancedFilters } from "./registry";
import { SavedViewFilters } from "../../../../services/grantsApi";
import { AlertFilters } from "../../../../types/grants";
import { normalizeRegion } from "../../../../constants/regions";

/**
 * UUID regex for filter sanitization (same as CallsFilters.tsx).
 */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Return type of the useFilterState hook.
 */
export interface UseFilterStateReturn {
  /** Current filter values keyed by plugin key */
  filterValues: Record<string, unknown>;
  /** Set a single filter value (updates URL) */
  setFilterValue: (key: string, value: unknown) => void;
  /** Clear all filters to defaults (updates URL) */
  clearAllFilters: () => void;
  /** Generate API query params from current filter values */
  toApiParams: () => URLSearchParams;
  /** Generate SavedViewFilters from current filter values */
  toSavedViewFilters: () => SavedViewFilters;
  /** Apply a saved view by setting URL params from SavedViewFilters */
  applySavedView: (filters: SavedViewFilters) => void;
  /** Number of active advanced filter categories (0-N) */
  advancedFiltersCount: number;
  /** Whether any filter has a non-default value */
  hasActiveFilters: boolean;
  /** Generate sanitized AlertFilters from current filter values */
  toAlertFilters: () => AlertFilters;
}

/**
 * Central filter state hook for the plugin-based filter architecture.
 *
 * @param registry - The filter plugin registry
 * @returns Filter state and mutation functions
 */
export function useFilterState(
  registry: ReadonlyArray<FilterPlugin<unknown>>
): UseFilterStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read current values from URL for all plugins
  const filterValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    for (const plugin of registry) {
      values[plugin.key] = plugin.fromUrlParams(searchParams);
    }
    return values;
  }, [registry, searchParams]);

  // Build URL params from a complete set of filter values
  const buildUrlParams = useCallback(
    (values: Record<string, unknown>): URLSearchParams => {
      const params = new URLSearchParams();
      for (const plugin of registry) {
        const value = values[plugin.key] ?? plugin.defaultValue;
        if (plugin.isActive(value)) {
          plugin.toUrlParams(value, params);
        }
      }
      return params;
    },
    [registry]
  );

  // Set a single filter value
  const setFilterValue = useCallback(
    (key: string, value: unknown) => {
      const newValues = { ...filterValues, [key]: value };
      const params = buildUrlParams(newValues);
      setSearchParams(params, { replace: true });
    },
    [filterValues, buildUrlParams, setSearchParams]
  );

  // Clear all filters to defaults
  const clearAllFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Generate API query params
  const toApiParams = useCallback((): URLSearchParams => {
    const params = new URLSearchParams();
    for (const plugin of registry) {
      const value = filterValues[plugin.key];
      if (plugin.isActive(value)) {
        plugin.toApiParams(value, params);
      }
    }
    return params;
  }, [registry, filterValues]);

  // Generate SavedViewFilters
  const toSavedViewFilters = useCallback((): SavedViewFilters => {
    const filters: Partial<SavedViewFilters> = {};
    for (const plugin of registry) {
      const value = filterValues[plugin.key];
      if (plugin.isActive(value)) {
        plugin.toSavedView(value, filters);
      }
    }
    return filters as SavedViewFilters;
  }, [registry, filterValues]);

  // Apply saved view
  const applySavedView = useCallback(
    (filters: SavedViewFilters) => {
      const values: Record<string, unknown> = {};
      for (const plugin of registry) {
        values[plugin.key] = plugin.fromSavedView(filters);
      }
      const params = buildUrlParams(values);
      setSearchParams(params, { replace: true });
    },
    [registry, buildUrlParams, setSearchParams]
  );

  // Count active advanced filter categories
  const advancedFiltersCount = useMemo(() => {
    const advancedPlugins = getAdvancedFilters(registry);
    return advancedPlugins.filter((plugin) =>
      plugin.isActive(filterValues[plugin.key])
    ).length;
  }, [registry, filterValues]);

  // Whether any filter is active
  const hasActiveFilters = useMemo(() => {
    return registry.some((plugin) =>
      plugin.isActive(filterValues[plugin.key])
    );
  }, [registry, filterValues]);

  // Generate sanitized AlertFilters (preserves logic from CallsFilters.sanitizeFiltersForAlert)
  const toAlertFilters = useCallback((): AlertFilters => {
    const programValue = (filterValues["program"] as string[]) ?? [];
    const regionValue = (filterValues["region"] as string[]) ?? [];
    const districtValue = (filterValues["district"] as string[]) ?? [];

    return {
      programs: programValue
        .filter((id) => UUID_REGEX.test(id))
        .slice(0, 20),
      regions: regionValue
        .filter((r) => typeof r === "string" && r.length <= 50)
        .map(normalizeRegion)
        .slice(0, 16),
      districtSlugs: districtValue
        .filter((d) => typeof d === "string" && /^[a-z0-9-]+$/.test(d))
        .slice(0, 100),
    };
  }, [filterValues]);

  return {
    filterValues,
    setFilterValue,
    clearAllFilters,
    toApiParams,
    toSavedViewFilters,
    applySavedView,
    advancedFiltersCount,
    hasActiveFilters,
    toAlertFilters,
  };
}
